import {
  Badge,
  Button,
  FeaturedIcon,
  Modal,
} from '@unified-checkout/design-system';
import { Pen, TrashCan } from '@unified-checkout/design-system/icons/index';
import { useWidth } from '@unified-checkout/kit-components';
import { useEffect, useState } from 'react';
import { IAddress } from 'apps/customer/src/types/innerLayout/addresses';
import {
  useDeleteAddress,
  useEditAddress,
} from 'apps/customer/src/services/inner_layout/addresses';
import { MOBILE_BREAKPOINT } from 'apps/customer/src/constants';

interface IAddressCardItemProps {
  address: IAddress;
  selectedAddressId?: number;
  setSelectedAddressId?: (id: number) => void;
  isLoadingAddress: boolean;
}

export const AddressCardItem = ({
  address,
  selectedAddressId,
  setSelectedAddressId,
  isLoadingAddress,
}: IAddressCardItemProps) => {
  const isEditing = selectedAddressId === address.id;
  const isMobile = useWidth(MOBILE_BREAKPOINT);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const {
    mutate: deleteAddressMutation,
    isPending: isPendingDeleteAddress,
    isSuccess: isSuccessDeletingAddress,
  } = useDeleteAddress(address.id, address.address_type);
  const { mutate: editAddressMutation, isPending: isPendingEditAddress } =
    useEditAddress();

  const deleteAddressMutationHandler = () => {
    deleteAddressMutation();
  };
  const setDefaultHandler = () => {
    editAddressMutation({
      address_type: address.address_type,
      id: address.id,
      body: { is_default: true },
    });
  };
  useEffect(() => {
    if (isSuccessDeletingAddress) {
      setIsDeleteModalOpen(false);
    }
  }, [isSuccessDeletingAddress]);

  return (
    <section>
      <section>
        <div className="flex flex-col gap-3 bg-white md:flex-row md:items-center md:w-full md:justify-between ">
          <div className="md:flex md:flex-col md:gap-2 md:self-stretch">
            <p className="mb-1 text-gray-900 english-utility-label-semibold">
              {address.full_name}
            </p>
            <p className="text-gray-600 english-utility-label-compact-regular">
              {address.address_line_1}
            </p>
            {address.address_line_2 && (
              <p className="text-gray-600 english-utility-label-compact-regular">
                {address.address_line_2}
              </p>
            )}
            <p className="text-gray-600 english-utility-label-compact-regular">
            {[address.city, address.state, address.country].filter(Boolean).join(', ')}
            </p>
            <p className="text-gray-600 english-utility-label-compact-regular">
              Phone number : {address.phone_number}
            </p>
          </div>

          <section className="flex gap-2 md:gap-3 md:items-center md:justify-end shrink-0">
            {address.is_default ? (
              <div className="[&_*]:rounded-lg">
                <Badge text="Default" color="gray" size="lg" />
              </div>
            ) : (
              <Button
                text={address.is_default ? 'Default' : 'Set default'}
                size="sm"
                variant="outlined"
                color="secondary"
                onClickHandler={setDefaultHandler}
                disabled={
                  address.is_default || isPendingEditAddress || isLoadingAddress
                }
              />
            )}
            <Button
              text="Edit"
              size="sm"
              leadingIcon={Pen}
              variant="outlined"
              color="secondary"
              onClickHandler={() => setSelectedAddressId?.(address.id)}
              disabled={isEditing}
            />
            <Button
              text="Delete"
              size="sm"
              leadingIcon={TrashCan}
              variant="outlined"
              // disabled={address.is_default}
              color="secondary"
              onClickHandler={() => setIsDeleteModalOpen(true)}
            />
            <Modal
              open={isDeleteModalOpen}
              onCloseHandler={() => setIsDeleteModalOpen(false)}
              closeOnClickOutside
              isMobile={isMobile}
              supportingText="Address will be removed from the 1-Click Account."
              title="Are you sure you want to delete the address"
              headerIcon={
                <FeaturedIcon size="xl" color="red" icon={TrashCan} />
              }
              btnsProps={[
                {
                  color: 'primary',
                  text: 'Cancel',
                  onClickHandler: () => setIsDeleteModalOpen(false),
                },
                {
                  color: 'secondary',
                  text: 'Delete address',
                  variant: 'outlined',
                  loading: isPendingDeleteAddress,
                  onClickHandler: deleteAddressMutationHandler,
                },
              ]}
            />
          </section>
        </div>
      </section>
    </section>
  );
};
